import { isOnPromotion, PROMO_THRESHOLD } from '../measure/promotion';
import type { Day } from './day';
import { priceReachesFloor } from './depth';
import { returnWindowElapsed, withinReturnWindow } from './permanence';
import type { CampaignRun } from './wave';

// A return is a price back within PROMO_THRESHOLD of the regular price, or at least that far above the campaign price (CONTEXT.md, Confirmed campaign).
function hasReturned(price: number, run: CampaignRun): boolean {
  return (
    !isOnPromotion(price, run.regularPrice) ||
    priceReachesFloor(price, run.avgPriceDuring * (1 + PROMO_THRESHOLD))
  );
}

/** `permanent` is a price cut, not a campaign (CONTEXT.md, Confirmed campaign). */
export type CampaignStatus = 'confirmed' | 'provisional' | 'unknown' | 'permanent';

/** Whether the run's price came back within the return window after its end (CONTEXT.md, Confirmed campaign). */
export function confirmRun(
  days: readonly Day[],
  run: CampaignRun,
  today: string,
  lastObserved: string | null
): CampaignStatus {
  const end = days.findIndex((d) => d.date === run.endDate);
  for (let d = end + 1; d < days.length && withinReturnWindow(run.endDate, days[d].date); d++) {
    const { kind, price, regularSource } = days[d];
    if (kind !== 'regular' || price === null) continue;
    if (hasReturned(price, run)) return 'confirmed';
    if (regularSource === 'restart') return 'permanent';
  }
  if (!returnWindowElapsed(run.endDate, today)) return 'provisional';
  const windowFullyObserved =
    lastObserved !== null && returnWindowElapsed(run.endDate, lastObserved);
  return windowFullyObserved ? 'permanent' : 'unknown';
}
